import { memo } from 'react';
import type { AriaAttributes } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text } from '@library';
import styles from './Pagination.module.scss';
import PaginationButton from './PaginationButton';
import HorizontalLayout from './layout/HorizontalLayout';
import Button from './Button';
import ChevronRightIcon from './icons/ChevronRightIcon';
import ChevronLeftIcon from './icons/ChevronLeftIcon';

interface PaginationProps extends AriaAttributes {
    page: number;
    count: number;
    onChange(value: number): void;
}

const TOP_PAGINATION_ANCHOR_ELEMENT_ID = 'pagination-top-anchor-element';

export const PaginationTopAnchor = memo(() => {
    return <div id={TOP_PAGINATION_ANCHOR_ELEMENT_ID} />;
});

const PaginationList = memo(({ page, count, onChange }: PaginationProps) => {
    const showFirstPages = page < 3;
    const startList = page === 2 ? [1, 2, 3] : [1, 2];
    const endList =
        page === count - 1 ? [count - 2, count - 1, count] : [count - 1, count];
    const showLastPages = page > count - 2;

    return (
        <>
            {!showFirstPages ? (
                <>
                    <PaginationButton
                        page={page}
                        value={1}
                        onChange={onChange}
                    />
                    {page !== 3 && (
                        <div
                            className={styles.ellipses}
                            data-test-class="paginationSeparatorEllipses"
                        >
                            …
                        </div>
                    )}
                </>
            ) : (
                startList.map(pageValue => (
                    <PaginationButton
                        key={pageValue}
                        page={page}
                        value={pageValue}
                        onChange={onChange}
                    />
                ))
            )}
            {showLastPages &&
                endList.map(pageValue => (
                    <PaginationButton
                        key={pageValue}
                        page={page}
                        value={pageValue}
                        onChange={onChange}
                    />
                ))}
            {!showFirstPages && !showLastPages && (
                <>
                    <PaginationButton
                        page={page}
                        value={page - 1}
                        onChange={onChange}
                    />
                    <PaginationButton
                        page={page}
                        value={page}
                        onChange={onChange}
                    />
                    <PaginationButton
                        page={page}
                        value={page + 1}
                        onChange={onChange}
                    />
                </>
            )}
            {!showLastPages && (
                <>
                    {page !== count - 2 && (
                        <div
                            className={styles.ellipses}
                            data-test-class="paginationSeparatorEllipses"
                        >
                            …
                        </div>
                    )}
                    <PaginationButton
                        page={page}
                        value={count}
                        onChange={onChange}
                    />
                </>
            )}
        </>
    );
});

const DEFAULT_TOP_ELEMENT = document.querySelector('body');

const Pagination = ({
    autoScroll = false,
    page,
    count,
    onChange,
}: PaginationProps & {
    autoScroll?: boolean;
}) => {
    if (!count || count === 1) {
        return null;
    }

    const onPageChange = (value: number) => {
        onChange(value);

        if (page !== value && autoScroll) {
            const elem =
                document.getElementById(TOP_PAGINATION_ANCHOR_ELEMENT_ID) ||
                DEFAULT_TOP_ELEMENT;

            if (!elem) {
                return;
            }

            elem.scrollIntoView(true);
        }
    };

    return (
        <nav data-test-class="paginationNavigation">
            <HorizontalLayout align="center" gap="small">
                {page !== 1 && (
                    <Button
                        textOnly
                        noPadding
                        onPress={() => onPageChange(page - 1)}
                    >
                        <HorizontalLayout align="center" gap="micro">
                            <ChevronLeftIcon smallIcon />
                            <Text caption uppercase bold>
                                <span className={styles.pageNavigationText}>
                                    <FormattedMessage id="previous page" />
                                </span>
                            </Text>
                        </HorizontalLayout>
                    </Button>
                )}
                <HorizontalLayout align="center">
                    {count <= 5 ? (
                        Array.from({ length: count }, (_, i) => i + 1).map(
                            pageValue => (
                                <PaginationButton
                                    key={pageValue}
                                    page={page}
                                    value={pageValue}
                                    onChange={onPageChange}
                                />
                            )
                        )
                    ) : (
                        <PaginationList
                            page={page}
                            count={count}
                            onChange={onPageChange}
                        />
                    )}
                </HorizontalLayout>
                {page !== count && (
                    <Button
                        textOnly
                        noPadding
                        onPress={() => onPageChange(page + 1)}
                    >
                        <HorizontalLayout align="center" gap="micro">
                            <Text caption uppercase bold>
                                <span className={styles.pageNavigationText}>
                                    <FormattedMessage id="next page" />
                                </span>
                            </Text>
                            <ChevronRightIcon smallIcon />
                        </HorizontalLayout>
                    </Button>
                )}
            </HorizontalLayout>
        </nav>
    );
};

export default memo(Pagination);
